<script setup lang="ts">
    // Desktop renders the existing `Modal` untouched; mobile renders `UiActionSheet`
    // instead, driven by the same event-bus name. Both react to the same
    // `<name>:open` / `<name>:close` events a consumer wires via `useModal(name, …)` —
    // callers do not change how they open/close this modal, only its root tag.
    //
    // Why not always mount both and toggle visibility with CSS? The slotted content
    // is a live child tree (form inputs, their own composables), not inert markup —
    // rendering `<slot />` twice would instantiate it twice. So exactly one branch is
    // mounted at a time via `v-if`/`v-else`, same as the plan's own sketch. The
    // consequence: resizing across the `lg` boundary while this modal is open loses
    // its state (the newly mounted branch starts closed) — a known, accepted gap, not
    // a scenario real touch/desktop usage crosses mid-session.
    import { useListen, useEvent } from '@/composables/useEventBus';
    import { useIsMobileLayout } from '~/composables/useIsMobileLayout';

    const props = withDefaults(
        defineProps<{
            name: string;
            /**
             * The mobile sheet renders this as its visible title row; desktop's `Modal`
             * has no built-in title, so it is drawn here when `withDesktopTitle` is set
             * and is otherwise only the sheet's screen-reader name.
             */
            title: string;
            /**
             * Draws the desktop title row. Seven consumers used to write it out
             * themselves, each carrying a `useIsMobileLayout()` call and the same six
             * lines of markup; the copies had already drifted on the text colour.
             *
             * Opt-in rather than automatic because most consumers of this wrapper
             * genuinely have no title row (the preview, the contact picker), and turning
             * it on for everyone would give them one they never asked for.
             */
            withDesktopTitle?: boolean;
            /**
             * Theme key of the title row's container. The right-panel modal style lays its
             * header out differently, hence the override.
             */
            desktopTitleThemeKey?: string;
            modalStyle?: string;
            width?: number;
            scrollable?: boolean;
            /** See `UiActionSheet`'s own prop of the same name. Desktop ignores it. */
            maxHeightPercent?: number;
            /** Forwarded to `Modal` only — the mobile sheet has no equivalent slot wrapper to override. */
            contentClasses?: string;
        }>(),
        {
            modalStyle: 'centered',
            width: 600,
            scrollable: false,
            maxHeightPercent: 88,
            contentClasses: '',
            withDesktopTitle: false,
            desktopTitleThemeKey: 'modal.title.container',
        }
    );

    // `Modal.vue` emits these itself; consumers such as `Form/Modals/Library.vue`
    // (`@opened="onOpened"`, a one-time data-fetch latch) rely on them regardless
    // of which branch is mounted, so this wrapper re-emits the same four.
    const emit = defineEmits<{
        (e: 'before-open', payload: any): void;
        (e: 'opened', payload: any): void;
        (e: 'before-close', payload: any): void;
        (e: 'closed', payload: any): void;
    }>();

    const isMobileLayout = useIsMobileLayout();
    const { getModalZIndex } = useModalsStore();

    // Mirrors `Modal.vue`'s own open-state tracking (components/partials/Modal.vue):
    // that component only tracks its state internally and isn't rendered at all on
    // mobile, so the sheet branch below needs its own local `open` to bind to.
    const open = ref(false);
    useListen('modal:open', (name: string) => {
        if (name === props.name) open.value = true;
    });
    useListen('modal:close', (name: string) => {
        if (name === props.name) open.value = false;
    });

    // `Modal.vue` fires `before-open` ahead of its own `opened.value = true` and
    // `before-close` ahead of `opened.value = false`; here `open` above already
    // flipped by the time this watcher runs, so both events per transition fire
    // back-to-back rather than straddling the state change. No current consumer
    // (`Library.vue`'s `onOpened`) depends on that finer ordering.
    watch(open, (isOpen, wasOpen) => {
        if (!isMobileLayout.value) return;
        if (isOpen && !wasOpen) {
            emit('before-open', props.name);
            emit('opened', props.name);
        } else if (!isOpen && wasOpen) {
            emit('before-close', props.name);
            emit('closed', props.name);
        }
    });

    // The sheet's own dismiss gestures (backdrop tap, swipe, Escape) call this with
    // `false` — re-emit through the same bus event `Modal.vue`'s close button uses,
    // so `useModalsStore` unwinds its stack (z-index, overlay, body scroll lock).
    const onSheetOpenChange = (value: boolean) => {
        if (!value) useEvent('modal:close', props.name);
    };
</script>

<template>
    <Modal
        v-if="!isMobileLayout"
        :name="name"
        :modal-style="modalStyle"
        :width="width"
        :scrollable="scrollable"
        :content-classes="contentClasses"
        @before-open="emit('before-open', $event)"
        @opened="emit('opened', $event)"
        @before-close="emit('before-close', $event)"
        @closed="emit('closed', $event)"
    >
        <div
            v-if="withDesktopTitle"
            :class="$theme(desktopTitleThemeKey)"
        >
            <h2 class="font-bricolage font-extrabold text-[22px] leading-[26px] tracking-[-0.03em] text-black lg:text-2xl lg:font-semibold lg:tracking-normal lg:font-inter lg:text-gray-800">
                {{ title }}
            </h2>
        </div>
        <slot />
    </Modal>
    <UiActionSheet
        v-else
        :open="open"
        :title="title"
        :z-index="getModalZIndex(name)"
        :max-height-percent="maxHeightPercent"
        @update:open="onSheetOpenChange"
    >
        <slot />
    </UiActionSheet>
</template>
